

import type { ValidChainId } from "@bracket/chainography/types";
import { chainography } from "../../package.json";
import { isValidChainId, isURL, type URL, } from "@bracket/chainography/types";
import { runtime, } from "./runtime";
import { electRPCURL, } from "./electRPCURL";

/**
 * Gets the RPC URL for a specified blockchain network.
 * 
 * In browser environments, it first tries to get the URL from environment variables,
 * then falls back to the configuration in package.json.
 * 
 * In Node environments, it tries to get the URL from process.env, then falls back
 * to the configuration in package.json.
 * 
 * @param chain - The blockchain network ID to get the RPC URL for
 * @param silent - If true, returns null instead of throwing errors
 * @returns The RPC URL for the specified chaBROWSERin, or null if silent=true and an error occurs
 * @throws Error if the chain is invalid or no RPC URL can be found and silent=false
 */
export const getRPCURL = ( chain: ValidChainId, silent = false ): URL|null => {

	try {

		if(isValidChainId(chain)) {

			if(runtime() === "BROWSER") {
				const { hostname, } = globalThis.location;
				const ENVKEY = hostname.endsWith(".bracket.fi") && !hostname.startsWith("staging") || import.meta.env.PROD === "true" ? "PROD" : "DEVEL";
				const VARKEY = `VITE_${ENVKEY}_CHAIN_${chain}_RPC`;
				const RPCURL = import.meta.env[VARKEY];
				if(isURL(RPCURL)) {
					return RPCURL;
				}
				const url = chainography[`CHAIN_${chain}_RPC`];
				if(isURL(url)) {
					return url;
				}
				throw new Error(`@bracket/chainography configuration (package.json) does not have a dev chain set for chain ${chain}`)
			}

			if(runtime() === "NODE") {
				const url = globalThis.process.env[`CHAIN_${chain}_RPC`] || chainography[`CHAIN_${chain}_RPC`];
				if(isURL(url)) {
					return url;
				}
				throw new Error(`node environment failed to obtain a valid RPC on chain ${chain}`)
			}

			throw new Error(`unknown runtime ${runtime()} - expect either NODE or BROWSER`);

		}
		throw new Error(`invalid/unsupported chain ${chain}`)
	}
	catch( err ) {
		if(silent) return null;
		throw new Error(`@bracket/chainography::getRPCURL failed - ${err instanceof Error ? err.message : `unknown error`}`);
	}
}
